import React, { useState } from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import {
  CREATE_TERMINATION_INTAKE_REQUEST,
  GET_MONITORING_QUEUE_LIST,
} from '../../../utils/statApi/EntityTerminationApi';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import TerminationIntakeRequestForm from './TerminationIntakeRequestForm';
import { IEntityTerminationIntakeRequest } from '../types';

interface ITerminationIntakeRequestPanelProps {
  closePanel: () => void;
}

const TerminationIntakeRequestPanel = ({
  closePanel,
}: ITerminationIntakeRequestPanelProps): JSX.Element => {
  const intakeForm = useForm<IEntityTerminationIntakeRequest>();
  const { handleSubmit } = intakeForm;
  const [uploadIsInProgress, setUploadInProgress] = useState(false);

  const [createIntakeRequest, { loading, error }] = useMutation(CREATE_TERMINATION_INTAKE_REQUEST, {
    refetchQueries: [GET_MONITORING_QUEUE_LIST],
    onCompleted: () => {
      closePanel();
    },
  });

  const handleSubmitClick: SubmitHandler<IEntityTerminationIntakeRequest> = (formData): void => {
    createIntakeRequest({
      variables: {
        createIntakeRequestInputDto: {
          companyCode: formData.company[0].companyCode,
          businessReason: formData.businessReason,
          hasEmployees: formData.hasEmployees ?? null,
          hasRevenueContracts: formData.hasRevenueContracts ?? null,
          hasOperatingExpenses: formData.hasOtherOperatingExpenses ?? null,
          businessSponsorApprovalDocuments: formData.businessSponsorApprovalDocuments,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Termination Intake Request"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Termination Intake Request Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading || uploadIsInProgress}
          closePanel={closePanel}
          saveLabel="Submit"
          saveTitle="Submit"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSubmitClick)}
        />
      )}
    >
      <form onSubmit={handleSubmit(handleSubmitClick)}>
        <LoadingErrorMessage
          error={error}
          loading={loading}
          actionName="submitting the termination intake request"
        />
        <TerminationIntakeRequestForm form={intakeForm} setUploadInProgress={setUploadInProgress} />
      </form>
    </CoherencePanel>
  );
};

export default TerminationIntakeRequestPanel;
